import { useMemo } from "react";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import { translate } from "app/filters/i18nFilter";
import { renderIndustryDataAvailabilityNotice } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useDurationSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDurationSubtitleFilter";

import { getMapDimensionsByWindowWidth } from "../../configs/CountryMapWidgetConfigs";
import { CountryMapAndTable } from "../../CountryMapAndTable";

import { useIndustrySpendRank } from "./useIndustrySpendRank";

interface IIndustrySpendRankProps {
    category: IFlattenedCategory;
}

export const IndustrySpendRank = ({ category }: IIndustrySpendRankProps) => {
    const { data, isLoading, isError, availability } = useIndustrySpendRank(category);
    const durationSubtitleFilter = useDurationSubtitleFilter();
    const { width } = useWindowSize();
    const mapDimensions = useMemo(() => getMapDimensionsByWindowWidth(width ?? 0), [width]);

    const hasData = (data?.countrySpendShares?.length ?? 0) > 0;
    const showEmptyState = !isLoading && (!hasData || isError);

    return (
        <WidgetContainer
            title={translate("ad.intelligence.overview.countries.map.title.full")}
            tooltip={translate("ad.intelligence.overview.countries.map.tooltip.full")}
            subtitleFilters={[durationSubtitleFilter]}
            isLoading={isLoading}
            showSubtitleFiltersLoadingState={false}
            loaderHeight={mapDimensions.height}
            loaderWidth="100%"
            bodyMinHeight={mapDimensions.height}
            withBorder
            noData={showEmptyState}
            automationId="industry-spend-rank-widget"
            noticeSlot={renderIndustryDataAvailabilityNotice(availability)}
        >
            <CountryMapAndTable
                data={data}
                mapDimensions={mapDimensions}
            />
        </WidgetContainer>
    );
};
